import { ZERO } from '@morpho-org/dapp-core'
import { cn } from '@morpho-org/ui-v6/utils'
import { formatters } from '@morpho-org/ui-v6/utils'
import { forwardRef, type HTMLAttributes } from 'react'
import { formatUnits } from 'viem'

import { Donut, getDonutColor } from './Donut'

export interface CellAllocationProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Allocation percentage as bigint (18 decimals, where 1e18 = 100%) */
  allocation: bigint
  /** Index for color selection from allocation palette */
  index?: number
  /** Gap between donut and label in pixels */
  gap?: number
  /** Hide the percentage label */
  hideLabel?: boolean
  /** Percentage offset to rotate the start position (18 decimals) */
  percentageOffset?: bigint
}

/**
 * CellAllocation component - Tailwind replica of dapp-ui-v5 CellAllocation
 *
 * Displays a donut chart with allocation percentage.
 *
 * @example
 * ```tsx
 * // Basic usage - 55% allocation
 * <CellAllocation allocation={parseUnits("0.55", 18)} index={0} />
 *
 * // Without label
 * <CellAllocation allocation={parseUnits("0.25", 18)} index={1} hideLabel />
 *
 * // With offset (for stacked donuts)
 * <CellAllocation
 *   allocation={parseUnits("0.30", 18)}
 *   index={2}
 *   percentageOffset={parseUnits("0.55", 18)}
 * />
 * ```
 */
const CellAllocation = forwardRef<HTMLDivElement, CellAllocationProps>(
  (
    { allocation, index = 0, gap = 10, hideLabel = false, percentageOffset, className, ...props },
    ref
  ) => {
    const localizedAllocation = formatters.percent.digits(2).unit('%').of(allocation, 18)
    const formattedAllocation = Number(formatUnits(allocation, 18)) * 100
    const formattedPercentageOffset = Number(formatUnits(percentageOffset ?? ZERO, 18)) * 100
    const donutColor = getDonutColor(index)

    return (
      <div
        ref={ref}
        className={cn('flex h-[21px] items-center', className)}
        style={{ gap }}
        {...props}
      >
        <Donut
          allocation={formattedAllocation}
          color={donutColor}
          backgroundColor="var(--color-muted)"
          percentageOffset={formattedPercentageOffset}
        />
        {!hideLabel && (
          <span className="text-2xs text-foreground flex items-center leading-normal">
            {localizedAllocation}
          </span>
        )}
      </div>
    )
  }
)

CellAllocation.displayName = 'CellAllocation'

export { CellAllocation }
